

import React, { useState, useEffect } from 'react';
import ExcelTable from '../common/ExcelTable';
import { useSpace } from '../common/SpaceContextProvider';
import { useHistory } from 'react-router-dom';
import { Tooltip, OverlayTrigger, Form, Modal } from 'react-bootstrap';
import { CustomRowHorizontal } from '../common/ExcelTableComponents';
import { sum } from '../common/utils';
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import { allocateResources, formatDateToYYYYMMDD } from '../common/calculateResources';
import AlertCustom from '../common/AlertCustom';
import InfoIcon from '@mui/icons-material/Info';
import RangeScroller from '../common/RangeScroller';
import CustomLabelWithInfoIcon from '../common/CustomLabel';
import { fetchData, postData } from '../common/Api';
import DatePicker from "react-datepicker";
import CustomDataViewer from '../common/CustomDataViewer';
import CustomDataViewerGantt from '../common/CustomDataViewerGantt';
import CustomDataViewerCheckBox from '../common/CustomDataViewerCheckBox';

function getMonthsFrom(years=2, startingDateStr, endingDateStr) {

    const months = [];
    const currentYear = new Date().getFullYear();
    const startingMonth = startingDateStr === null || startingDateStr === undefined ? 
        new Date().getMonth() : new Date(startingDateStr).getMonth();
    
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];

    // Get months for current year
    for (let i = 0; i < years*12; i++) {

        const monthIndex = (startingMonth + i) % 12;
        const monthYear = Math.floor((startingMonth + i) / 12);
        const year = (currentYear + monthYear).toString().slice(-2);
        const month = monthNames[monthIndex] + "'" + year;

        // date with the end of the month
        const date = new Date(currentYear + monthYear, monthIndex + 1, 0);
        const dateString = formatDateToYYYYMMDD(date);
        
        // if ending date is set, check if the month is after the ending date
        const endDate = endingDateStr === undefined || endingDateStr === null ? null : new Date(endingDateStr);

        if (date > endDate && endDate !== null) {
            break;
        }
        months.push({name: month, id: i, date: dateString});    
   }

   return months;
}


const AllocationTab = ({id}) => {

    const history = useHistory();
    const { teams, projects, shirts, teamsProjects } = useSpace();
    const [projectsData, setProjectsData] = useState([]);
    const [teamsData, setTeamsData] = useState([]);
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [initTeamsData, setInitTeamsData] = useState([]);
    const [initProjectsData, setInitProjectsData] = useState([]);
    const [mode, setMode] = useState("manual"); 
    const [expanded, setExpanded] = useState(false);
    const [allowFlexTime, setAllowFlexTime] = useState(false);
    const [alpha, setAlpha] = useState(10);
    const [beta, setBeta] = useState(1);
    const [amountOfYears, setAmountOfYears] = useState(2);
    const [startingDate, setStartingDate] = useState(() => {
        const currentDate = new Date();
        return formatDateToYYYYMMDD(currentDate);
    });
    const [endingDate, setEndingDate] = useState(null);
    const [months, setMonths] = useState(getMonthsFrom(amountOfYears));
    const [dateSelectedFromConsole, setDateSelectedFromConsole] = useState(false); 
    const [showModalSave, setShowModalSave] = useState(false);
    const [showGantt, setShowGantt] = useState(false);
   
    const handleSetStartingDate = ( date ) => {

        const deadlines = projects.filter(project => 
            project.deadline !== null).map(project => 
                new Date(project.deadline));

        const minDeadline = deadlines.length > 0 ? 
            new Date(Math.min(...deadlines)) : new Date();
        
        if (date < minDeadline) {
            setStartingDate(formatDateToYYYYMMDD(date));
            setDateSelectedFromConsole(true);
        } else {
            setError(`Starting date shlould not be bigger than the min deadline 
                ${formatDateToYYYYMMDD(minDeadline)}`);
        }
    }

    const getProjectsAllocationsData = async () => {
        
        try {

            const response = await fetchData(`/api/v1/spaces/${id}/allocation`);
            const serverData = response.data;

            serverData?.settings?.alpha && setAlpha(serverData.settings.alpha);
            serverData?.settings?.beta && setAlpha(serverData.settings.beta);
            serverData?.settings?.flex_time_allowed && setAllowFlexTime(serverData.settings.flex_time_allowed);
            serverData?.settings?.alpha && setMode('automatic');
 
            if (serverData?.data?.length > 0) {

                const startDate = serverData?.settings?.starting_date;
                const endDate = serverData?.settings?.ending_date;
                const m = getMonthsFrom(amountOfYears, startDate, endDate);
  
                const matrix = Array(projects.length).fill().map(() => 
                    Array(m.length).fill().map(() => 
                        ({ value: '', className: '', tooltip: '', checked: false})));

                serverData.data.forEach(element => {
                    const rowid = projects.findIndex(obj => obj.id === element.project_id);
                    const colid = m.findIndex(obj => obj.name === element.month);
                    matrix[rowid][colid] = {value: element.value === 1 ? 'x' : '', 
                        className: '', tooltip: '', checked: element.value === 1 ? true : false};
                });

                setProjectsData(JSON.parse(JSON.stringify(matrix)));
                setInitProjectsData(JSON.parse(JSON.stringify(matrix)));
                setStartingDate(startDate);
                setEndingDate(endDate);
                setDateSelectedFromConsole(false);
            } else {
                const m = getMonthsFrom(amountOfYears, startingDate);
                const matrix = Array(projects.length).fill().map(() => 
                    Array(m.length).fill().map(() => 
                        ({ value: '', className: '', tooltip: '', checked: false})));        
                setProjectsData(JSON.parse(JSON.stringify(matrix)));
                setInitProjectsData(JSON.parse(JSON.stringify(matrix)));
            }
        } catch (error) {
            setError(`Error fetching projects allocation data :: ${error.message}`);
            const m = getMonthsFrom(amountOfYears, startingDate);
            const matrix = Array(projects.length).fill().map(() => 
                Array(m.length).fill().map(() => 
                    ({ value: '', className: '', tooltip: '', checked: false})));        
            setProjectsData(JSON.parse(JSON.stringify(matrix)));
            setInitProjectsData(JSON.parse(JSON.stringify(matrix)));
        } 
    }

    const saveData = async () => {

        const url = `/api/v1/spaces/${id}/allocation`;

        const dat = projectsData.map((row, i) => row.map((cell, j) => {
            return cell.value !== '' ? {
                row_id: i, 
                col_id: j,
                project_id: projects[i].id,
                month: months[j].name,
                month_id: months[j].id,
                value: 1} : null
        }));

        const datFilt = dat.map(
            item => item.filter(
            element => element !== null )).filter(
                item => item.length > 0);

        const payload = {
            data: datFilt,
            settings: {
                alpha: mode === 'manual' ? null : alpha, 
                beta:  mode === 'manual' ? null : beta, 
                flex_time_allowed:  mode === 'manual' ? null : allowFlexTime, 
                starting_date: months[0].date, 
                ending_date: months[months.length - 1].date}
        }

        try {
            await postData(url, payload);
        } catch (error) {
            setError(`Error saving data :: ${error.message} ${error.response?.data?.message}`);
            console.log(error);
        }

        const fetchData = async () => {
            await getProjectsAllocationsData();
        }
        fetchData();

    }

    useEffect(() => {

        const getData = async () => {
            await getProjectsAllocationsData();
        }
        if (projects.length > 0 && JSON.stringify(projectsData) === JSON.stringify(initProjectsData)) {
            getData();
        } 

    }, [projects, months]);


    useEffect(() => {

        const matrix = Array(teams.length).fill().map(() => 
            Array(months.length).fill().map(() => 
                ({ value: '', className: '', tooltip: '', readOnly: true, checked: false })));
        
        matrix.forEach((row, i) => {
            const membersNumb = teams[i].members;
            row.forEach((cell, j) => {
                cell.value = membersNumb;
        })});
        
        setTeamsData(JSON.parse(JSON.stringify(matrix)));
        setInitTeamsData(JSON.parse(JSON.stringify(matrix)));

    }, [teams, months]);


    useEffect(() => {

        const m = getMonthsFrom(amountOfYears, startingDate, endingDate);
        setMonths(m);

        if (dateSelectedFromConsole){

            // reset the data in the table ---> TODO:: CHANGE THIS TO JUST SHIFT THE DATA TO SHOW, do not remove!!!!
            const matrixProjects = Array(projects.length).fill().map(() => 
                Array(m.length).fill().map(() => 
                    ({ value: '', className: '', tooltip: '', checked: false })));
            setProjectsData(JSON.parse(JSON.stringify(matrixProjects)));

            // reset the data in the teams table
            const matrix = Array(teams.length).fill().map(() => 
                Array(m.length).fill().map(() => 
                    ({ value: '', className: '', tooltip: '', readOnly: true, checked: false })));
            
            matrix.forEach((row, i) => {
                const membersNumb = teams[i].members;
                row.forEach((cell, j) => {
                    cell.value = membersNumb;
            })});
            
            setTeamsData(JSON.parse(JSON.stringify(matrix)));
            setInitTeamsData(JSON.parse(JSON.stringify(matrix)));
            
        } 

    }, [startingDate]);

    useEffect(() => {

        if (JSON.stringify(initProjectsData) !== JSON.stringify(projectsData)) {
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }

        if (teamsProjects.length === teams.length 
            && teams.length > 0 
            && projectsData.length === projects.length
            && projects.length > 0) {

            var teamsDataCopy = [...teamsData];

            const totalMonthsAllocatedForProjects = projectsData.map((project, i) => {
                return project.map((month, j) => {
                    return month.value !== '' ? 1 : 0;
                }).reduce((a, b) => a + b, 0);
            });

            teamsData.forEach((row, i) => {

                var teamSize = teams[i].members;
                
                row.forEach((col, j) => {

                    const projectsPerTeam = teamsProjects[i].map((element, index) => {
                        element.project_id = index;
                        return element;}).filter(element => element.value !== '');
                    
                    var projectsForAGivenMonth = [];
                    projectsData.forEach((project, projectId) => 
                        project.forEach((projectMonth, monthId) => {
                        if (monthId === j && projectMonth.value !== '') {
                            return projectsForAGivenMonth.push(projectId);
                        } 
                    })); 

                    const projectsPerTeamInAGivenMonth = projectsPerTeam.filter(element => {
                        return projectsForAGivenMonth.includes(element.project_id) 
                    });

                    const resources = projectsPerTeamInAGivenMonth.map(element => {
                        const tot = totalMonthsAllocatedForProjects[element.project_id] > 0 ? 
                            totalMonthsAllocatedForProjects[element.project_id] : 1;
                        return shirts.filter(shirt => 
                            shirt.name === element.value).map(shirt => 
                                shirt.resource_mos / tot)[0]
                    });

                    var newCell = teamSize - sum(resources);

                    const isUpdatedValue = newCell !== Number(initTeamsData[i][j].value);

                    teamsDataCopy[i][j].className = newCell < 0 ? 
                        'invalid-cell' : isUpdatedValue ? 'valid-cell' : '';
                    teamsDataCopy[i][j].tooltip = newCell < 0 ? 
                        'Negative value means resources in the team are \
                         overallocated for a given month.' : isUpdatedValue ? 
                         'Value updated according to the allocations made in projects.' : '';
                    teamsDataCopy[i][j].value = Number(newCell.toFixed(2));
                });
            });

            setTeamsData(teamsDataCopy);

        }
    }, [projectsData]);

    const tooltip = (
        <Tooltip id="allocation-tab-tooltip">
            First, mark how long it will take to complete each project.
            You can do it by ticking the box in each project-month cell.
            You will see the allocations of the workload prefilled automatically
            by the tool in the panel below that contains allocation of the team 
            per month.
        </Tooltip>
      );

    const checkProjectsDeadlines = () => {
        const projectsWithNoDeadline = projects.filter(project =>
            project.deadline === null);
        if (projectsWithNoDeadline.length > 0) {
            alert('Some projects have no deadline - will use randomly assigned deadline.');
        }
    }

    return (

        <div className='tab-container'>
            <div className='tab-container-main-panel'>
            <div className='form-group-custom'>
                <IconButton style={{ margin: '0rem', padding: '0rem'}} 
                    onClick={() => setExpanded(!expanded)}>
                    {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
                <p className='mt-1 mb-2 font-size-larger'><b>Allocate resources</b></p>
            </div>
            
            {/* beginning of the form for selecting the mode settings */}
            { expanded && 
            <div className='mt-1 mb-2 flex-row-align-center'>

                <Form.Group className='small-container'>
                    <CustomLabelWithInfoIcon placeTooltip="right" label="Choose mode" 
                        info='In manual mode a user places the allocations by him/her-self.
                        In automatic mode the tool will allocate the projects to months 
                        automatically based on the pre-defined deadlines and then use that
                        to calculate the teams resource allocations.'
                    />
                    <div className='flex-row'>
                        <Form.Check className='m-2'
                            type="radio"
                            label="Manual"
                            name="allocationMode"
                            value="manual"
                            checked={mode === "manual"}
                            onChange={() => setMode("manual")}
                        />
                        <Form.Check className='m-2'
                            type="radio"
                            label="Automatic"
                            name="allocationMode"
                            value="automatic"
                            checked={mode === "automatic"}
                            onChange={() => setMode("automatic")}
                        />
                    </div>
                </Form.Group>
                
                <RangeScroller
                    min={0}
                    max={10}
                    step={1}
                    value={alpha}
                    setValue={setAlpha}
                    label="Select alpha"
                    tooltip="Alpha (Overuse Penalty) controls how heavily to penalize over-allocating team members. 
                    Higher values make the system avoid assigning more work than a team can handle."
                    disabled={mode === 'manual'}
                />

                <RangeScroller
                    min={0}
                    max={10}
                    step={1}
                    value={beta}
                    setValue={setBeta}
                    label="Select beta"
                    tooltip="Beta (Utilization Reward) controls how much to reward efficient use of available team members. 
                    Higher values encourage full utilization of resources."
                    disabled={mode === 'manual'}
                />

                <div className='small-container' style={{opacity: mode === 'manual' ? 0.5 : 1}}>
                    <Form.Group controlId="flexibleTime">
                    <Form.Check className='m-2'
                        type="checkbox"
                        checked={allowFlexTime}
                        label="Flexible time allowed"
                        onChange={(e) => setAllowFlexTime(e.target.checked)}
                    />
                    </Form.Group>
                    <div className="btn-custom reset-btn" disabled={mode==='manual'} 
                    onClick={()=> {
                        setAlpha(10);
                        setBeta(1);
                        setAllowFlexTime(false);
                    }}>Reset Settings</div>
                </div>

                <button className='btn-custom green' 
                    disabled={mode === 'manual'}
                    onClick={() => {
                        checkProjectsDeadlines();
                        const result = allocateResources(
                            projects, 
                            teams, 
                            shirts, 
                            months, 
                            teamsProjects, 
                            allowFlexTime,
                            alpha, 
                            beta
                        );
                        setProjectsData(result.projects);
                        // if (JSON.stringify(result.teams) === JSON.stringify(initTeamsData)) {
                        //     setShowAlert(true);
                        //     setError('Teams allocations seem not to be changed - check your shirts assignments to projects-teams.');
                        // }
                        setTeamsData(result.teams);
                        setError(null);
                        setShowAlert(false);
                }}>Run</button>

            </div>

            }

            {/* end of form for selecting mode */}

            <p className='mt-2 mb-2 font-size-larger'><b>Projects Completion Planning</b></p>
            {
            (projectsData.length > 0 && projectsData[0].length === months.length) ? 
            <>
                <div className='justify-content-between'>
                    <div className='mb-1 mt-1 flex-row-align-center'>
                        <CustomLabelWithInfoIcon 
                            placeTooltip="bottom" 
                            info="Select starting date of the planning period. The default is current date." 
                            label="Select starting date"
                        />
                        <DatePicker
                        dateFormat="MM/yyyy"
                        showMonthYearPicker
                        showFullMonthYearPicker
                        selected={startingDate} 
                        onChange={(date) => handleSetStartingDate(date)}
                        className='form-control m-1' />
                    </div>
                    <button className='btn-custom blue m-2' 
                            onClick={() => {setShowGantt(!showGantt)}}
                            >{showGantt ? 'Table View' : 'Gantt Chart'}
                    </button>
                </div>

                <div className="spreadsheet-container">
                <ExcelTable 
                    data={projectsData}
                    setData={setProjectsData}
                    colsItems={months} 
                    rowsItems={projects}
                    Row={CustomRowHorizontal}
                    CustomDataViewer={showGantt ? CustomDataViewerGantt : CustomDataViewerCheckBox}
                    isCheckBox={showGantt ? false : true}
                />

                <br/>
                </div> </>: <div>No projects created yet.</div>
            }
            <br/>
            <p className='mt-2 mb-2 font-size-larger'><b>Team Resource Allocation</b></p>
            {
            (teamsData.length > 0 && teamsData[0].length === months.length) ? 
                <div className="spreadsheet-container-full">
                <ExcelTable 
                    data={teamsData}
                    setData={setTeamsData}
                    colsItems={months} 
                    rowsItems={teams}
                    Row={CustomRowHorizontal}
                    CustomDataViewer={CustomDataViewer}
                /> <br/>
                </div> : <div>No teams created yet.</div>
            }

            {
                (teams.length > 0 && projects.length > 0)? <>
                    <div className='btn-group-custom'>                    
                        <button className="btn-custom green" 
                        onClick={() => setShowModalSave(true)}>Save</button>
                        <button className='btn-custom blue'
                            onClick={() => {
                            const matrix = Array(projects.length).fill().map(() => 
                                Array(months.length).fill().map(() => 
                                    ({ value: "", className: "", tooltip: "", checked: false })));
                            setProjectsData(JSON.parse(JSON.stringify(matrix)));
                        }}
                        >Clear All Data</button>
                    </div>

                    <div className='flex-row-center'>
                        <Modal show={showModalSave} onHide={() => setShowModalSave(false)}>
                            <Modal.Header closeButton>
                                <Modal.Title>Save Allocation Data</Modal.Title>
                            </Modal.Header>
                            <Modal.Body style={{ textAlign: 'justify'}}>Are you sure you want to save your allocation data?</Modal.Body>
                            <Modal.Footer className='modal-footer flex-row-center'>
                                <button className='btn-custom-flex blue' onClick={() => {
                                    setShowModalSave(false);
                                }}>No</button>
                                <button className='btn-custom-flex green' onClick={() => {
                                    saveData();
                                    setShowModalSave(false);
                                }}>Yes</button>
                            </Modal.Footer>
                        </Modal>
                    </div>

                <AlertCustom 
                text="Resource allocation data not saved" 
                variant="danger"
                showAlert={showAlert}
                setShowAlert={setShowAlert}
                />
                </> : null
            }

            </div>
            <div className='switch-list-table-view-container'>
                <OverlayTrigger placement="bottom" overlay={tooltip}>
                    <InfoIcon className='switch-view'></InfoIcon>
                </OverlayTrigger>
            </div>

            {error && <AlertCustom 
                text={error}
                variant="danger"
                showAlert={showAlert && error}
                setShowAlert={setShowAlert}
            />}
        </div>
    )
}

export default AllocationTab;